import { pedidosApi } from "../../page/OrderResumePag/_services/api";

// Workaround until a dedicated shipping-quote endpoint exists: add the item
// to the cart to read deliveryOptions, then restore the prior cart state
// (decrement by 1 if the SKU was already there, delete the line otherwise).
export async function fetchShippingQuote({ sku, cep, customerUuid, fatorFrete }) {
  const channel = process.env.REACT_APP_DEFAULT_CHANNEL;

  let prevQuantity = 0;
  let existingDeliveryOptions = null;
  try {
    const getRes = await pedidosApi.get(`v2/carrinho/${channel}`, {
      params: { customerUuid, cep, fator: fatorFrete },
    });
    const carts = getRes.data?.data?.items || {};
    for (const cartData of Object.values(carts)) {
      const found = (cartData?.items || []).find((i) => i?.sku === sku);
      if (found) {
        prevQuantity = parseInt(found.quantity, 10) || 0;
        existingDeliveryOptions = cartData?.deliveryOptions || null;
        break;
      }
    }
  } catch (e) {
    prevQuantity = 0;
    existingDeliveryOptions = null;
  }

  // If the SKU is already in the cart, reuse the deliveryOptions from the
  // existing cart entry instead of mutating the cart (POST/PATCH). This
  // avoids an incorrect re-calculation when the user returns from the
  // checkout summary screen.
  if (prevQuantity > 0 && existingDeliveryOptions) {
    return { deliveryOptions: existingDeliveryOptions, prevQuantity, warningMessage: null };
  }

  let postRes;
  try {
    postRes = await pedidosApi.post(`v2/carrinho/${channel}`, {
      customerUuid,
      cep,
      sku,
      quantity: 1,
      fator: fatorFrete,
    });
  } catch (postErr) {
    // If the POST itself fails (e.g. 500), the item may have been partially
    // added. Attempt an explicit removal so the cart doesn't stay dirty.
    try {
      await pedidosApi.delete(`v2/carrinho/${channel}`, {
        data: { customerUuid, sku, cep, fator: fatorFrete },
      });
    } catch (cleanupErr) {
      console.error("Failed to cleanup cart after failed shipping quote POST:", cleanupErr);
    }
    throw postErr;
  }

  // `items` is keyed by brand_uuid and may contain pre-existing cart entries
  // from other brands. Look up the brand that actually owns the SKU we just
  // quoted so we don't pick up another product's deliveryOptions.
  const items = postRes.data?.data?.items || {};
  let deliveryOptions = null;
  for (const brand of Object.values(items)) {
    const hasSku = (brand?.items || []).some((i) => i?.sku === sku);
    if (hasSku) {
      deliveryOptions = brand?.deliveryOptions || null;
      break;
    }
  }

  // When the backend rejects the SKU for this CEP it goes into `warnings`
  // (same payload) with `error: true` and the offending SKU in `data`.
  let warningMessage = null;
  if (!deliveryOptions) {
    const warnings = postRes.data?.data?.warnings || {};
    for (const brandWarnings of Object.values(warnings)) {
      const match = (brandWarnings || []).find(
        (w) => w?.error && (w?.data || []).some((p) => p?.sku === sku)
      );
      if (match) {
        warningMessage = match.message || null;
        break;
      }
    }
  }

  try {
    if (prevQuantity > 0) {
      await pedidosApi.patch(`v2/carrinho/${channel}`, {
        customerUuid,
        sku,
        cep,
        quantity: -1,
      });
    } else {
      await pedidosApi.delete(`v2/carrinho/${channel}`, {
        data: { customerUuid, sku, cep, fator: fatorFrete },
      });
    }
  } catch (e) {
    console.error("Failed to restore cart after shipping quote:", e);
  }

  return { deliveryOptions, prevQuantity, warningMessage };
}
